import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type { RootLocationConfiguration } from '@cognite/apm-config';

import type { CogniteClient } from '@cognite/sdk';

import type { IChecklistSchedulerService } from './checklist-scheduler-service-interface';
import type {
  SchedulerChecklist,
  SchedulerDataModelFlag,
  SchedulerNode,
} from './types';
import { getDateFilters } from './utils/utils';
import type { DateRangeType } from '../../ui/date-range-picker/types';

dayjs.extend(utc);

function hasItems<T>(data: unknown): data is { items: T[] } {
  return (
    data != null &&
    typeof data === 'object' &&
    'items' in data &&
    Array.isArray((data as { items: unknown }).items)
  );
}

export type CogniteClientDependencies = Pick<
  CogniteClient,
  'get' | 'post' | 'project'
>;

export class ChecklistSchedulerService implements IChecklistSchedulerService {
  client: CogniteClientDependencies;
  project: string;
  schedulerDataModelFlag: SchedulerDataModelFlag;

  constructor(client: CogniteClientDependencies) {
    this.client = client;
    this.project = client.project;
    this.schedulerDataModelFlag = 'apm';
  }

  async getChecklists(
    selectedRootLocation: RootLocationConfiguration,
    selectedDateFilter: DateRangeType
  ): Promise<SchedulerChecklist[]> {
    const { fromISO, untilISO } = getDateFilters(selectedDateFilter);
    const rootLocation = selectedRootLocation.assetExternalId;
    const appInstanceSpace = selectedRootLocation.appDataInstanceSpace;
    const sourceInstanceSpace = selectedRootLocation.sourceDataInstanceSpace;

    const response = await this.client.get<{
      items: SchedulerChecklist[];
    }>(
      `/api/v1/projects/${this.project}/checklistgenerator/checklists?rootLocation=${rootLocation}&appInstanceSpace=${appInstanceSpace}&sourceInstanceSpace=${sourceInstanceSpace}&fromTime=${fromISO}&untilTime=${untilISO}&dataModel=${this.schedulerDataModelFlag}`,
      {
        headers: {
          'cdf-version': 'alpha',
        },
        retryValidator: false,
      }
    );

    if (!hasItems<SchedulerChecklist>(response?.data)) {
      throw new Error(
        'Invalid API response: missing items array in getChecklists'
      );
    }

    return response.data.items;
  }

  async getChecklistsByTemplateIds(
    selectedRootLocation: RootLocationConfiguration,
    selectedDateFilter: DateRangeType,
    templateIds: SchedulerNode[]
  ): Promise<SchedulerChecklist[]> {
    const { fromISO, untilISO } = getDateFilters(selectedDateFilter);
    const appInstanceSpace = selectedRootLocation.appDataInstanceSpace;

    const response = await this.client.post<{
      items: SchedulerChecklist[];
    }>(
      `/api/v1/projects/${this.project}/checklistgenerator/checklists/filter`,
      {
        headers: {
          'cdf-version': 'alpha',
        },
        retryValidator: false,
        data: {
          templates: templateIds,
          appInstanceSpace,
          fromTime: fromISO,
          untilTime: untilISO,
        },
      }
    );

    if (!hasItems<SchedulerChecklist>(response?.data)) {
      throw new Error(
        'Invalid API response: missing items array in getChecklistsByTemplateIds'
      );
    }

    return response.data.items;
  }

  async createMissingChecklists(
    plannedChecklists: SchedulerChecklist[]
  ): Promise<SchedulerNode[]> {
    const response = await this.client.post<{
      items: SchedulerNode[];
    }>(`/api/v1/projects/${this.project}/checklistgenerator/checklists`, {
      data: {
        items: plannedChecklists,
        dataModel: this.schedulerDataModelFlag,
      },
      headers: {
        'cdf-version': 'alpha',
      },
      retryValidator: false,
    });

    if (!hasItems<SchedulerNode>(response?.data)) {
      throw new Error(
        'Invalid API response: missing items array in createMissingChecklists'
      );
    }

    return response.data.items;
  }
}

export const createChecklistSchedulerService = (
  ...args: ConstructorParameters<typeof ChecklistSchedulerService>
): IChecklistSchedulerService => new ChecklistSchedulerService(...args);
